import React, { useState } from 'react';
import InputMask from 'react-input-mask-next';
import { Loader, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { RegistrationData, Address } from './types';
import { emptyAddress } from './types';

interface Props {
  data: RegistrationData;
  registrationId: string;
  onComplete: (data: Partial<RegistrationData>) => void;
}

export default function PersonalDataStep({ data, registrationId, onComplete }: Props) {
  const [name, setName] = useState(data.name);
  const [cpf, setCpf] = useState(data.cpf);
  const [phone, setPhone] = useState(data.phone);
  const [pixKey, setPixKey] = useState(data.pixKey);
  const [bank, setBank] = useState(data.bank);
  const [serviceEntryTime, setServiceEntryTime] = useState(data.serviceEntryTime);
  const [serviceExitTime, setServiceExitTime] = useState(data.serviceExitTime);
  const [endRes, setEndRes] = useState<Address>(data.enderecoResidencial);
  const [endCom, setEndCom] = useState<Address>(data.enderecoComercial);
  const [loadingCep, setLoadingCep] = useState({ residential: false, commercial: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const fetchCep = async (cep: string, type: 'residential' | 'commercial') => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setLoadingCep(prev => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const d = await res.json();
      if (!d.erro) {
        const addr = { cep, logradouro: d.logradouro || '', numero: '', complemento: '', bairro: d.bairro || '', cidade: d.localidade || '', estado: d.uf || '' };
        if (type === 'residential') setEndRes(addr);
        else setEndCom(addr);
      }
    } catch {}
    finally { setLoadingCep(prev => ({ ...prev, [type]: false })); }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Nome obrigatório';
    if (cpf.replace(/\D/g, '').length !== 11) errs.cpf = 'CPF inválido';
    if (phone.replace(/\D/g, '').length < 10) errs.phone = 'Telefone inválido';
    if (!endRes.logradouro) errs.endRes = 'Endereço residencial obrigatório';
    if (!endRes.numero) errs.endResNum = 'Número obrigatório';
    if (!endRes.cidade) errs.endResCidade = 'Cidade obrigatória';
    if (!endCom.logradouro) errs.endCom = 'Endereço comercial obrigatório';
    if (!endCom.numero) errs.endComNum = 'Número obrigatório';
    if (!endCom.cidade) errs.endComCidade = 'Cidade obrigatória';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const residentialAddress = `${endRes.logradouro}, ${endRes.numero}${endRes.complemento ? `, ${endRes.complemento}` : ''} - ${endRes.bairro}, ${endRes.cidade}/${endRes.estado} - CEP: ${endRes.cep}`;
      const commercialAddress = `${endCom.logradouro}, ${endCom.numero}${endCom.complemento ? `, ${endCom.complemento}` : ''} - ${endCom.bairro}, ${endCom.cidade}/${endCom.estado} - CEP: ${endCom.cep}`;

      await supabase
        .from('client_self_registrations')
        .update({
          client_name: name.trim(),
          cpf: cpf.replace(/\D/g, ''),
          phone: phone.replace(/\D/g, ''),
          address: residentialAddress,
          business_address: commercialAddress,
          residential_cep: endRes.cep,
          residential_logradouro: endRes.logradouro,
          residential_numero: endRes.numero,
          residential_complemento: endRes.complemento,
          residential_bairro: endRes.bairro,
          residential_cidade: endRes.cidade,
          residential_estado: endRes.estado,
          commercial_cep: endCom.cep,
          commercial_logradouro: endCom.logradouro,
          commercial_numero: endCom.numero,
          commercial_complemento: endCom.complemento,
          commercial_bairro: endCom.bairro,
          commercial_cidade: endCom.cidade,
          commercial_estado: endCom.estado,
          pix_key: pixKey.trim(),
          bank: bank.trim(),
          service_entry_time: serviceEntryTime,
          service_exit_time: serviceExitTime,
        })
        .eq('id', registrationId);

      onComplete({
        name: name.trim(),
        cpf: cpf.replace(/\D/g, ''),
        phone: phone.replace(/\D/g, ''),
        enderecoResidencial: endRes,
        enderecoComercial: endCom,
        pixKey: pixKey.trim(),
        bank: bank.trim(),
        serviceEntryTime,
        serviceExitTime,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (errKey: string) =>
    `w-full px-4 py-3 border rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${errors[errKey] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;

  const renderAddress = (label: string, addr: Address, setAddr: (a: Address) => void, loadingKey: 'residential' | 'commercial', errPrefix: string) => (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{label}</h3>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
        <div className="relative">
          <InputMask
            mask="99999-999"
            value={addr.cep}
            onChange={(e) => {
              const val = e.target.value;
              setAddr({ ...addr, cep: val });
              fetchCep(val, loadingKey);
            }}
            className={inputClass('')}
            placeholder="00000-000"
          />
          {loadingCep[loadingKey] && (
            <Loader className="absolute right-3 top-3.5 w-4 h-4 animate-spin text-gray-400" />
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro *</label>
        <input
          type="text"
          value={addr.logradouro}
          onChange={(e) => setAddr({ ...addr, logradouro: e.target.value })}
          className={inputClass(errPrefix)}
          placeholder="Rua, Avenida..."
        />
        {errors[errPrefix] && <p className="text-red-500 text-xs mt-1">{errors[errPrefix]}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
          <input
            type="text"
            value={addr.numero}
            onChange={(e) => setAddr({ ...addr, numero: e.target.value })}
            className={inputClass(`${errPrefix}Num`)}
            placeholder="123"
          />
          {errors[`${errPrefix}Num`] && <p className="text-red-500 text-xs mt-1">{errors[`${errPrefix}Num`]}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
          <input
            type="text"
            value={addr.complemento}
            onChange={(e) => setAddr({ ...addr, complemento: e.target.value })}
            className={inputClass('')}
            placeholder="Apto, Bloco..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
        <input
          type="text"
          value={addr.bairro}
          onChange={(e) => setAddr({ ...addr, bairro: e.target.value })}
          className={inputClass('')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
          <input
            type="text"
            value={addr.cidade}
            onChange={(e) => setAddr({ ...addr, cidade: e.target.value })}
            className={inputClass(`${errPrefix}Cidade`)}
          />
          {errors[`${errPrefix}Cidade`] && <p className="text-red-500 text-xs mt-1">{errors[`${errPrefix}Cidade`]}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
          <input
            type="text"
            value={addr.estado}
            maxLength={2}
            onChange={(e) => setAddr({ ...addr, estado: e.target.value.toUpperCase() })}
            className={inputClass('')}
            placeholder="SP"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Dados Pessoais</h2>
        <p className="text-gray-600 text-sm">Preencha todas as informações corretamente.</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass('name')}
            placeholder="Seu nome completo"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
            <InputMask
              mask="999.999.999-99"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              className={inputClass('cpf')}
              placeholder="000.000.000-00"
            />
            {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
            <InputMask
              mask="(99) 99999-9999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputClass('phone')}
              placeholder="(00) 00000-0000"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chave PIX</label>
            <input
              type="text"
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              className={inputClass('')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
            <input
              type="text"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              className={inputClass('')}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entrada no serviço</label>
            <InputMask
              mask="99:99"
              value={serviceEntryTime}
              onChange={(e) => setServiceEntryTime(e.target.value)}
              className={inputClass('')}
              placeholder="08:00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Saída do serviço</label>
            <InputMask
              mask="99:99"
              value={serviceExitTime}
              onChange={(e) => setServiceExitTime(e.target.value)}
              className={inputClass('')}
              placeholder="17:00"
            />
          </div>
        </div>

        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Endereço Residencial</span>
          </div>
          {renderAddress('', endRes, setEndRes, 'residential', 'endRes')}
        </div>

        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Endereço Comercial</span>
          </div>
          {renderAddress('', endCom, setEndCom, 'commercial', 'endCom')}
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={saving}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving ? <><Loader className="w-5 h-5 animate-spin" /> Salvando...</> : 'Continuar'}
      </button>
    </div>
  );
}
